import { type Validator } from "redux-form";
import formatCurrency from "Utils/formatCurrency";
import xss, { whiteList } from "xss";

import { calculateAge } from "./dateHelpers";

export const composeValidators =
  (...validators) =>
  (value, allValues, meta) =>
    validators.reduce((error, validator) => error || validator(value, allValues, meta), undefined);

export const composeParsers =
  (...parsers) =>
  (value) => {
    return parsers.reduce((currentValue, parser) => {
      return parser(currentValue);
    }, value);
  };

// Validator to handle sanitization
export const escapeHTML = (value: string) => {
  const options = {
    whiteList,
    stripIgnoreTag: true, // Strip all tags not in the whitelist
    stripIgnoreTagBody: ["script"], // Strip the body of "script" tags
  };
  if (typeof value === "string") {
    return xss(value, options);
  }
  return value;
};

export const validateRequired: Validator = (value: string | null | undefined): string | null | undefined => {
  if (value === undefined || value === null || value.toString().trim() === "") {
    return "Required";
  }
  return undefined;
};

export const validateRequiredSignature: Validator = (value: string | null | undefined): string | null | undefined => {
  if (value === undefined || value === null || value.toString().trim() === "") {
    return "Signature Required";
  }
  return undefined;
};

export const validateSigned: Validator = (value: string | null | undefined): string | null | undefined => {
  if (value === undefined || value === null || value.toString().trim() === "") {
    return " ";
  }
  return undefined;
};

export const validateEmail: Validator = (value: string | null | undefined): string | null | undefined =>
  value && !/^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i.test(value) ? "Invalid email address" : undefined;

export const validateZipCodeArray: Validator = (value: string | null | undefined): string | null | undefined => {
  if (value) {
    const zipCodes = value
      .toString()
      .split(",")
      .map((zip) => zip.trim());
    const zipCodePattern = /^\d{5}(-\d{4})?$/;
    for (const zipCode of zipCodes) {
      if (zipCode && !zipCodePattern.test(zipCode.trim())) {
        return `Invalid ZIP code format: ${zipCode}`;
      }
    }
  }
  return undefined;
};

export const validateUrl: Validator = (value: string | null | undefined): string | null | undefined =>
  value && !/^(ftp|http|https):\/\/[^ "]+$/i.test(value) ? "Invalid Url" : undefined;

type ParseDateResult = { error: string } | { date: Date };

export const parseDate = (value: string): ParseDateResult => {
  const [month, day, year] = value.split("/").map((s) => parseInt(s, 10));
  if ([day, month, year].every((i) => i >= 0)) {
    // Taken mostly from https://stackoverflow.com/a/6178341
    // Check the ranges of month and year
    if (year < 1000 || year > 3000) {
      return { error: "Invalid year" };
    }

    if (month == 0 || month > 12) {
      return { error: "Invalid month" };
    }

    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
      monthLength[1] = 29;
    }

    // Check the range of the day
    if (day <= 0 || day > monthLength[month - 1]) {
      return { error: "Invalid day" };
    }

    return { date: new Date(year, month - 1, day) };
  }
  return { error: "Invalid date" };
};

/**
 * Coerces numeric values to numbers, otherwise returns strings (these rff validations only run on strings)
 * Required in some cases where numeric fields do not work as expected with older validation fns
 */
export function parseNonNegativeNumber(value: string | number | null | undefined): string | number {
  if (value === undefined || value === null) {
    return value;
  }
  // Trim strings just in case
  const stringVal = String(value).trim();

  // If empty, let validations handle "required" or "numeric"
  if (stringVal === "") {
    return "";
  }

  const floatVal = parseFloat(stringVal);

  // If NaN, keep it as the raw string so validateNumber will fail
  if (Number.isNaN(floatVal)) {
    return stringVal;
  }

  // If negative, clamp to 0
  return Math.max(floatVal, 0);
}

export const validateDate: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  const result = parseDate(value);
  if (result.date) {
    return undefined;
  }
  return result.error;
};

export const validateAfterToday: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  const now = new Date().setHours(0, 0, 0, 0).valueOf();
  const result = parseDate(value);
  if (result.date) {
    return result.date.valueOf() >= now ? undefined : "Date must be on or after today";
  }
  return result.error;
};

export const validateBeforeToday: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  const now = new Date().setHours(0, 0, 0, 0).valueOf();
  const result = parseDate(value);
  if (result.date) {
    return result.date.valueOf() <= now ? undefined : "Date must be on or before today";
  }
  return result.error;
};

export const validateDependentAge: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  return calculateAge(new Date(value)) <= 26 ? undefined : "Dependent must 26 years old or younger";
};

export const validateFirstOfMonth: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  const day = value.split("/").map((s) => parseInt(s, 10))[1];

  if (day === 1) {
    return undefined;
  }
  return "Target effective date must be the first of the month.";
};

export const validateDateEstablished: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  const result = parseDate(value);
  if (result.date) {
    return result.date.valueOf() > new Date("1800") ? undefined : "Date must be after the year 1800";
  }
  return result.error;
};

export const validateCompanyName: Validator = (value: string | null | undefined): string | null | undefined =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? undefined : "Company Name can't be an Email Address";

export const validatePassword: Validator = (value: string | null | undefined): string | null | undefined =>
  value && value.length < 6 ? "Password must be at least 6 characters" : undefined;

export const validateName: Validator = (value: string | null | undefined): string | null | undefined =>
  value && value.trim().indexOf(" ") == -1 ? "Must contain First and Last Name" : undefined;

export const validateMiddleInitial: Validator = (value: string | null | undefined): string | null | undefined =>
  value && value.length > 1 ? "Middle Initial must be only 1 character" : undefined;

export const validatePhone: Validator = (value: string | null | undefined): string | null | undefined =>
  value && value.replace(/\D/g, "").length < 10 ? "Phone number must contain at least 10 digits" : undefined;

export const validateFein: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  value = value.replace(/\D/g, "");
  if (["00", "07", "08", "09", "17", "18", "19", "28", "29", "49", "78", "79", "89", "96", "97"].includes(value.substring(0, 2))) {
    return "FEIN/EIN Number is not valid";
  }
  if (value.length != 9) {
    return "FEIN/EIN must contain at least 9 digits";
  }
  if (["000000000", "111111111", "222222222", "333333333", "444444444", "555555555", "666666666", "777777777", "888888888", "999999999"].includes(value)) {
    return "FEIN/EIN Number is not valid";
  }
  return undefined;
};

export const validateSSN: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return undefined;
  }
  value = value.replace(/\D/g, "");
  if (value.length != 9) {
    return "SSN must contain 9 digits";
  }
  if (["123456789", "000000000", "111111111", "333333333"].includes(value)) {
    return "invalid social security number";
  }
  return undefined;
};

export const validateRoutingNumber: Validator = (value: string | null | undefined): string | null | undefined =>
  value && value.replace(/\D/g, "").length != 9 ? "Routing Number must contain 9 digits" : undefined;

export const validateMin =
  (min: number) =>
  (value: number | null | undefined): string | null | undefined =>
    value !== undefined && value !== null && value < min ? `Must be greater than or equal to ${min}` : undefined;

export const validateZipCode: Validator = (value: string | null | undefined): string | null | undefined =>
  value && value.replace(/\D/g, "").length != 5 ? "Zip Code must contain 5 digits" : undefined;

/*eslint-disable */
export const validatePhysicalAddress = (value: string | null | undefined): string | null | undefined => {
  const POBoxRegExp = new RegExp(
    /^ *((#\d+)|((box|bin)[-. \/\\]?\d+)|(.*\bp[ \.]? ?(o|0)[-. \/\\]? *-?((box|bin)|b|(#|num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(number|num|#)? *\d+|(num|number|#) *\d+)/,
    "i",
  );

  return value && POBoxRegExp.test(value) ? "Please enter a physical address, not a PO Box." : undefined;
};
/* eslint-enable */

export const validateMax =
  (max: number) =>
  (value: number | null | undefined): string | null | undefined =>
    value && parseFloat(value.toString().replace(/,/g, "")) > max ? `Must be less than or equal to ${max}` : undefined;

export const validateMaxDollar =
  (max: number) =>
  (value: number | null | undefined): string | null | undefined =>
    value && parseFloat(value.toString().replace(/,/g, "")) > max ? `Must be less than or equal to ${formatCurrency(max)}` : undefined;

export const validateNumber: Validator = (value: string | null | undefined): string | null | undefined =>
  value && isNaN(parseFloat(value)) && !isFinite(value) ? "Must enter a number" : undefined;

export const validateZero = validateMin(0);

export const validateHundred = validateMax(100);

export const validateBornAt: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return;
  }
  const result: Record<string, any> = parseDate(value);
  if (result.error) {
    return;
  }
  const bornAt: Date = result.date;
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 150);
  return bornAt < minDate || new Date() < bornAt ? "Birth date is invalid" : undefined;
};

export const validateHiredAt: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return;
  }
  const result: Record<string, any> = parseDate(value);
  if (result.error) {
    return;
  }
  const hiredAt: Date = result.date;
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);
  return hiredAt < minDate ? `Hire date must be after ${minDate.toLocaleDateString("en-US")}` : undefined;
};

export const validateChecked: Validator = (value: boolean | null | undefined): string | null | undefined => (value ? undefined : "Required");

export const validateWithinAMonthAgo: Validator = (value: string | null | undefined): string | void => {
  if (!value) {
    return;
  }
  const result: Record<string, any> = parseDate(value);
  if (result.error) {
    return;
  }
  let returnVal;
  const { date } = result;
  const today: Date = new Date();
  const minDate = new Date().setDate(today.getDate() - 31);
  if (date < minDate) {
    returnVal = "Date is more than 31 days ago";
  } else if (new Date() < date) {
    returnVal = "Date cannot be after today";
  }
  return returnVal;
};

export const validateNotPolygamy: Validator = (value: string | null | undefined): string | null | undefined => {
  if (value === "domestic_partner" || value === "spouse") {
    return "You can't have two spouses or domestic partners as dependents.";
  }
  return undefined;
};

export const validateNoEmoji: Validator = (value: string): string | null | undefined => {
  const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

  return value && emojiRegex.test(value) ? "Cannot contain emoji" : undefined;
};

export const validateUbi: Validator = (value: string | null | undefined): string | null | undefined => {
  if (!value) return undefined;

  const digits = value.replace(/\D/g, "");
  if (digits.length !== 9) {
    return "UBI must contain exactly 9 digits";
  }

  if (["000000000", "111111111", "222222222", "333333333", "444444444", "555555555", "666666666", "777777777", "888888888", "999999999"].includes(value)) {
    return "UBI number is not valid";
  }

  return undefined;
};
